import React from 'react';

import { ActionIcon, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

import { DATE_FORMAT_OPTIONS } from '../constants/date-constants';
import { useGetExperiment } from '../store/store';
import { capitalizeFirstLetter, formatDate } from '../utils';
import { LoadingContainer } from './LoadingContainer';
import { getClonotypeDefinitionLabel } from '../store/slices/preprocessing.slice';

interface ExperimentOverviewPanelProps {
  /** Experiment ID to fetch and display */
  experimentId: string;
  /** Callback when close button is clicked */
  onClose: () => void;
}

export function ExperimentOverviewPanel({ experimentId, onClose }: ExperimentOverviewPanelProps) {
  // Fetch experiment data using the same hook as parent
  const { data: experiment, isLoading } = useGetExperiment({
    id: experimentId,
  });

  if (isLoading || !experiment) {
    return (
      <div className="sidebar-panel flex items-center justify-center">
        <LoadingContainer height={200} />
      </div>
    );
  }
  return (
    <div className="sidebar-panel">
      {/* Title with close button */}
      <div className="sidebar-header">
        <Text className="text-sidebar-title">Experiment overview</Text>
        <ActionIcon size="xs" variant="subtle" onClick={onClose} className="text-sidebar-text">
          <IconX size={14} />
        </ActionIcon>
      </div>

      {/* Date and Description */}
      <Stack gap="xs" className="sidebar-content">
        <Text className="text-sidebar">{formatDate(experiment.creationDate, DATE_FORMAT_OPTIONS.FULL_DATE)}</Text>
        {experiment.description && (
          <div className="sidebar-description-box" tabIndex={0} role="group" aria-label="Experiment description">
            <Text className="text-sidebar">{experiment.description}</Text>
          </div>
        )}
      </Stack>

      {/* Organism and Clonotyping method */}
      <div className="sidebar-metadata">
        <Text className="text-sidebar">Organism:</Text>
        <Text className="text-sidebar">{capitalizeFirstLetter(experiment.organism)}</Text>
        <Text className="text-sidebar">Clonotyping method:</Text>
        <Text className="text-sidebar">{experiment.clonotypingMethods?.[0] ? getClonotypeDefinitionLabel(experiment.clonotypingMethods[0]) : '-'}</Text>
      </div>
    </div>
  );
}
